import React, { cloneElement, ReactElement, useRef } from 'react';
import uniqueId from 'lodash/uniqueId';
import styled, { CSSProperties } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { BreakpointProp } from '../../utils/styles/breakpoints';
import { Box, BoxProps } from '../layout/Box';
import { Flex, FlexProps } from '../layout/Flex';
import { Typography } from '../typography/Typography';

const Label = styled(Typography).withConfig({
    displayName: 'Label',
    shouldForwardProp: shouldIgnoreProp('required'),
})`
    user-select: none;
`;

export enum LabelPlacement {
    LEFT = 'left',
    RIGHT = 'right',
}

function getLabelWrapperProps(labelPlacement: LabelPlacement): Partial<FlexProps> | undefined {
    if (labelPlacement === LabelPlacement.LEFT) {
        return { direction: 'row', alignItems: 'center', justifyContent: 'space-between' };
    }

    if (labelPlacement === LabelPlacement.RIGHT) {
        return {
            direction: 'row-reverse',
            alignItems: 'center',
            justifyContent: 'left',
        };
    }
}

type FormControlLabelProps = Partial<BoxProps> & {
    label: ReactElement | string;
    labelPlacement?: LabelPlacement;
    labelWidth?: BreakpointProp<CSSProperties['width'] | number>;
    control: ReactElement;
    spacing?: BreakpointProp<number>;
    labelVerticalPadding?: number;
    disabled?: boolean;
};

export const FormControlLabel = (props: FormControlLabelProps) => {
    const {
        label,
        borderBottom,
        labelVerticalPadding = 2,
        labelPlacement = LabelPlacement.RIGHT,
        labelWidth = 'auto',
        control,
        spacing,
        ...other
    } = props;
    const labelIdRef = useRef(uniqueId('form-field-'));

    return (
        <Box width="100%" {...other}>
            <Flex flexGrow={1} spacing={spacing ?? 2} {...getLabelWrapperProps(labelPlacement)}>
                {label ? (
                    <Label
                        as="label"
                        htmlFor={labelIdRef.current}
                        variant="body"
                        color="primary"
                        width={labelPlacement === LabelPlacement.RIGHT ? '100%' : labelWidth}
                        py={labelVerticalPadding}
                        borderBottom={borderBottom}
                    >
                        {label}
                    </Label>
                ) : null}
                {cloneElement(control, {
                    ...props,
                    id: labelIdRef.current,
                })}
            </Flex>
        </Box>
    );
};
